/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, type ReactNode } from "react";
import { Select } from "./ui/Select";

export type PickerOption = { value: string; label: string };

/** The organization side: a fixed list, or a server search when the registry is the source. */
export type OrganizationPick = {
  value: string;
  onChange: (value: string) => void;
  options?: PickerOption[];
  fetchOptions?: (search: string) => Promise<PickerOption[]>;
  initialOption?: PickerOption | null;
  placeholder?: string;
};

/**
 * The provider side depends on the organization: the list is what the chosen organization
 * offers, and a choice that list no longer carries falls back to `fallback`: a fixed value, or a
 * rule over the current options (the fax review takes the first candidate).
 */
export type ProviderPick = {
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  fallback: string | ((options: PickerOption[]) => string);
  placeholder?: string;
};

/**
 * The one way staff name who referred a family: the referring organization, then a provider that
 * belongs to it. Used by the fax review (candidates from the intake agent, plus "create new") and
 * by the New referral page (the registry). The slots render extra fields under either select.
 */
export function OrganizationProviderPicker({
  organization,
  provider,
  organizationHint,
  providerHint,
  organizationExtra,
  providerExtra,
}: {
  organization: OrganizationPick;
  provider: ProviderPick;
  organizationHint?: string;
  providerHint?: string;
  organizationExtra?: ReactNode;
  providerExtra?: ReactNode;
}) {
  const providerOffered = provider.options.some((option) => option.value === provider.value);
  const effectiveProvider = providerOffered
    ? provider.value
    : typeof provider.fallback === "function"
      ? provider.fallback(provider.options)
      : provider.fallback;

  useEffect(() => {
    if (effectiveProvider !== provider.value) {
      provider.onChange(effectiveProvider);
    }
  }, [effectiveProvider, provider]);

  return (
    <>
      <section className="space-y-3">
        <h3 className="font-display text-base text-text-primary">Referring organization</h3>
        <Select
          label="Organization"
          value={organization.value}
          onChange={organization.onChange}
          {...(organization.options === undefined ? {} : { options: organization.options })}
          {...(organization.fetchOptions === undefined
            ? {}
            : { fetchOptions: organization.fetchOptions, searchable: true })}
          {...(organization.initialOption === undefined
            ? {}
            : { initialOption: organization.initialOption })}
          {...(organization.placeholder === undefined
            ? {}
            : { placeholder: organization.placeholder })}
        />
        {organizationHint && <p className="text-sm text-text-secondary">{organizationHint}</p>}
        {organizationExtra}
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-base text-text-primary">Referring provider</h3>
        <Select
          label="Provider"
          value={effectiveProvider}
          onChange={provider.onChange}
          options={provider.options}
          disabled={provider.options.length === 0}
          {...(provider.placeholder === undefined ? {} : { placeholder: provider.placeholder })}
        />
        {providerHint && <p className="text-sm text-text-secondary">{providerHint}</p>}
        {providerExtra}
      </section>
    </>
  );
}
